import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { FOOTER_LINKS } from "../../data";
import { scrollTo, EASE } from "../../lib/anim";

export const Footer = () => {
  const [policyOpen, setPolicyOpen] = useState(false);

  return (
    <footer className="relative bg-[#060607] border-t border-white/10 pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          <div className="lg:col-span-5">
            <button
              onClick={() => scrollTo("hero")}
              className="hover:opacity-80 transition-opacity duration-300"
              data-testid="footer-logo"
              aria-label="MGB Aviation"
            >
              <img src="/logo/mgb-aviation-primary.png" alt="MGB Aviation" title="MGB Aviation" draggable="false" className="h-9 sm:h-12 w-auto" />
            </button>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow mb-6">Brands</p>
            <ul className="space-y-3">
              {FOOTER_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-link-${l.label.replace(/[^a-z]/gi, "").toLowerCase()}`}
                    className="group inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-300"
                  >
                    <span className="h-px w-0 bg-white group-hover:w-4 transition-all duration-300" />
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="eyebrow mb-6">Contatti</p>
            <ul className="space-y-5">
              <li>
                <span className="block eyebrow text-white/30 mb-1">Email</span>
                <a
                  href="mailto:info@mgbaviation.com"
                  data-testid="footer-email"
                  className="font-display text-lg text-white hover:text-white/60 transition-colors duration-300"
                >
                  info@mgbaviation.com
                </a>
              </li>
              <li>
                <span className="block eyebrow text-white/30 mb-1">PEC</span>
                <a
                  href="mailto:mgbaviation@pec.it"
                  data-testid="footer-pec"
                  className="font-display text-lg text-white hover:text-white/60 transition-colors duration-300"
                >
                  mgbaviation@pec.it
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
            <p className="font-mono-x text-[11px] tracking-[0.2em] text-white/30">
              © MGB Aviation — All Rights Reserved.
            </p>
            <p className="font-mono-x text-[11px] tracking-[0.2em] text-white/30" data-testid="footer-vat">
              P.IVA IT13672210963
            </p>
          </div>
          <button
            onClick={() => setPolicyOpen(true)}
            data-testid="privacy-cookie-btn"
            className="font-mono-x text-[11px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors duration-300"
          >
            Privacy &amp; Cookie Policy
          </button>
        </div>
      </div>

      <AnimatePresence>
        {policyOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8"
            data-testid="privacy-modal"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPolicyOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="relative w-full max-w-2xl max-h-[82vh] overflow-y-auto border border-white/12 bg-[#0c0d0f] p-8 sm:p-10"
            >
              <div className="flex items-start justify-between gap-6 mb-8">
                <div>
                  <p className="eyebrow mb-3">Legal</p>
                  <h3 className="font-display text-3xl font-semibold tracking-tight text-white">
                    Privacy &amp; Cookie Policy
                  </h3>
                </div>
                <button
                  onClick={() => setPolicyOpen(false)}
                  data-testid="privacy-close"
                  className="text-white/50 hover:text-white transition-colors"
                  aria-label="Chiudi"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="space-y-6 text-sm leading-relaxed text-white/55">
                <div>
                  <h4 className="font-display text-white text-base mb-2">Titolare del trattamento</h4>
                  <p>
                    MGB Aviation — P.IVA IT13672210963. Per qualsiasi richiesta relativa ai dati
                    personali è possibile scrivere a info@mgbaviation.com o mgbaviation@pec.it.
                  </p>
                </div>
                <div>
                  <h4 className="font-display text-white text-base mb-2">Dati raccolti</h4>
                  <p>
                    Raccogliamo esclusivamente i dati che l'utente ci comunica volontariamente
                    contattandoci via email (nome, indirizzo email e contenuto del messaggio),
                    trattati per rispondere alle richieste e gestire i rapporti con partner e clienti.
                  </p>
                </div>
                <div>
                  <h4 className="font-display text-white text-base mb-2">Cookie</h4>
                  <p>
                    Questo sito utilizza esclusivamente cookie tecnici necessari al corretto
                    funzionamento delle pagine. Non vengono impiegati cookie di profilazione a fini
                    pubblicitari. Eventuali cookie analitici sono utilizzati in forma aggregata e
                    anonima.
                  </p>
                </div>
                <div>
                  <h4 className="font-display text-white text-base mb-2">Base giuridica e conservazione</h4>
                  <p>
                    Il trattamento si fonda sul consenso e sul legittimo interesse. I dati sono
                    conservati per il tempo strettamente necessario alle finalità indicate e
                    successivamente cancellati.
                  </p>
                </div>
                <div>
                  <h4 className="font-display text-white text-base mb-2">Diritti dell'interessato</h4>
                  <p>
                    Ai sensi del Regolamento (UE) 2016/679 (GDPR), l'utente può in qualsiasi momento
                    esercitare i diritti di accesso, rettifica, cancellazione, limitazione e
                    opposizione al trattamento, scrivendo ai recapiti sopra indicati.
                  </p>
                </div>
                <p className="text-white/30 text-xs pt-2">
                  Documento informativo di sintesi. Ultimo aggiornamento: 2026.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};
